import React, { useEffect, useState } from "react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import ShareBox from "../../components/share/ShareBox.jsx";
import { getReferralSummary } from "../../services/engagementService";
import { money } from "../../utils/format.js";

const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

/** Dashboard > Referral Credit: the filer's link, counts and unexpired credits. */
const ReferralCredit = () => {
  const [summary, setSummary] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    getReferralSummary()
      .then((s) => active && setSummary(s))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="max-w-[700px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="mb-5">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Finance
        </div>
        <h1 className="text-[28px] leading-[1.1] mt-1">Referral Credit</h1>
      </div>

      <Panel padded className="mb-5">
        {failed && <p className="text-[13px] text-[var(--danger)] m-0">We couldn't load your referral details. Please refresh.</p>}
        {!summary && !failed && <p className="text-[13px] text-[var(--muted)] m-0">Loading…</p>}
        {summary && (
          <>
            <p className="text-[13px] text-[var(--muted)] mt-0 mb-3">
              Share your link. When a friend who signed up with it gets their first return accepted by the IRS (and paid
              for), you earn a <b>{money(summary.creditValue)}</b> credit - the one-truck service fee. Credits stack, come
              off the service fee of your next original or amended return automatically, and last until the end of the
              next tax period. Up to {summary.capPerTaxPeriod} credits per tax period (July–June).
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                ["Available", money(summary.availableAmount)],
                ["Signed up", summary.signedUp],
                ["Waiting", summary.pending],
                [`Earned this period`, `${summary.earnedThisTaxPeriod} / ${summary.capPerTaxPeriod}`],
              ].map(([label, value]) => (
                <div key={label} className="rounded-[10px] bg-[var(--panel-2)] border border-[var(--line)] px-3 py-2">
                  <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--muted)] font-semibold">{label}</div>
                  <div className="text-[17px] font-bold text-[var(--ink)]">{value}</div>
                </div>
              ))}
            </div>
            {summary.credits.length > 0 && (
              <div className="text-[13px] mb-4">
                {summary.credits.map((c, i) => (
                  <div key={i} className="flex justify-between border-b border-[var(--line)] py-1.5 last:border-b-0">
                    <span className="text-[var(--muted)]">
                      {money(c.remaining)} left of {money(c.amount)}
                    </span>
                    <span className="text-[var(--muted)]">usable through {fmtDate(c.expiresOn)}</span>
                  </div>
                ))}
              </div>
            )}
            <ShareBox refCode={summary.code} placement="referrals" title="Your share link" intro={null} />
          </>
        )}
      </Panel>
    </div>
  );
};

export default ReferralCredit;
